/**
 * PhotoEditor builder — verbatim from v1 (research/03 §7, §15.6/15.7;
 * REQ-ELEM-5). window.openEditor / previewPhoto / the shared img_data closure
 * are defined AT BUILD TIME with last-instance-wins capture; fixed ids
 * `photoeditor`/`file-preview`/`previewloader` collide across instances.
 * data-isrequired is intentionally unquoted. The upload path passes no
 * fileInput/abortSignal (no abort-on-reselect, no clear-on-failure).
 *
 * jQuery split (research/01 Q1): bare $ = BUNDLED jQuery;
 * all jQuery is the BUNDLED copy (DIV-004).
 */
import $ from "jquery";
import { appState } from "../state/app-state";
import { uploadFileToAPI } from "../api/file-upload";
import { loadStyle } from "../bootstrap/load-style";
import { getConditionalDisplay, getToolTipHtml , getElementWidthClass, t } from "./base";

/**
 * DIV-025: Pintura lives in its own file (dist-v2/pintura-editor.js, see
 * elements/pintura-editor-entry.ts) and is fetched the first time a shopper
 * opens the editor, never on page load. One request per page, shared by every
 * photo-editor element on it; a failed load is retried on the next click.
 */
let pinturaLoading: Promise<NonNullable<Window["cpcPinturaEditor"]>> | undefined;
function loadPinturaEditor(): Promise<NonNullable<Window["cpcPinturaEditor"]>> {
  if (window.cpcPinturaEditor) return Promise.resolve(window.cpcPinturaEditor);
  // DIV-036: the editor's stylesheet travels with it (was in plugins.css). Requested
  // first so it is in place when the script registers; a failed stylesheet only
  // warns (loadStyle resolves either way) — the editor still opens, unstyled.
  loadStyle(`${appState.cdnURL}/pintura-editor.css`, "cpc-pintura-css");
  pinturaLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${appState.cdnURL}/pintura-editor.js`;
    script.onload = () => {
      if (window.cpcPinturaEditor) resolve(window.cpcPinturaEditor);
      else reject(new Error("pintura-editor.js loaded but registered nothing"));
    };
    script.onerror = () => reject(new Error("pintura-editor.js failed to load"));
    document.head.appendChild(script);
  }).catch((error) => {
    pinturaLoading = undefined; // let the next click try again
    throw error;
  }) as Promise<NonNullable<Window["cpcPinturaEditor"]>>;
  return pinturaLoading;
}

// Global function to upload photo editor images
window.uploadPhotoEditorImage = async function (file: any, propertyName: any) {
  const statusSpan = $(".cpc-photoeditor-upload-status");
  const hiddenInput = $(
    `input.cpc-photoeditor-url-input[data-property-name="${propertyName}"]`,
  );

  try {
    const fileUrl = await uploadFileToAPI(file, propertyName, statusSpan);
    hiddenInput.val(fileUrl);
    return fileUrl;
  } catch (error) {
    hiddenInput.val("");
    throw error;
  }
};

export function buildPhotoPicker(element: any): string {
  var isrequired = element.isRequired;
  var conditionalDisplay = getConditionalDisplay(element);
  let editButtonProps = element.properties ?? {};
  let toolTipHtml = getToolTipHtml(element, "span");

  let img_data: any = "";

  // The open editor, so it can be torn down. See releaseWebGLContexts below for
  // why destroy() alone is not enough (#72).
  let editorInstance: any = null;

  /**
   * The WebGL contexts Pintura created while an editor was open, so they can be
   * handed back when it closes. #72.
   *
   * Pintura never releases them: `loseContext` appears nowhere in the package,
   * and its own cleanup calls getContext("2d") on a WebGL canvas, which returns
   * null per spec and so no-ops. Measured across open -> finish -> reopen, live
   * contexts went 3 -> 4 -> 5 and none reported isContextLost(). Browsers keep
   * roughly 16 and then evict the OLDEST — including ones still on screen.
   *
   * They have to be caught at CREATION rather than found afterwards: Pintura
   * builds its canvases detached (`h("canvas")`, never appended), so walking the
   * document finds at most one of them. That is why this wraps getContext
   * instead of querying the DOM — an earlier attempt at the DOM walk released
   * 1 of 5.
   */
  let trackedContexts: WebGLRenderingContext[] = [];
  let originalGetContext: typeof HTMLCanvasElement.prototype.getContext | null = null;
  let installedGetContext: typeof HTMLCanvasElement.prototype.getContext | null = null;

  const startTrackingContexts = () => {
    if (originalGetContext) return; // already tracking
    const original = HTMLCanvasElement.prototype.getContext;
    const wrapper = function (this: HTMLCanvasElement, type: any, ...rest: any[]) {
      const context = (original as any).apply(this, [type, ...rest]);
      try {
        if (typeof type === "string" && type.indexOf("webgl") === 0 && context && trackedContexts.indexOf(context) === -1) {
          trackedContexts.push(context);
        }
      } catch {
        // tracking is best-effort — never break a canvas the page needs
      }
      return context;
    };
    originalGetContext = original;
    installedGetContext = wrapper as typeof HTMLCanvasElement.prototype.getContext;
    HTMLCanvasElement.prototype.getContext = installedGetContext;
  };

  const stopTrackingContexts = () => {
    if (!originalGetContext) return;
    // Restore ONLY if ours is still the installed function. A merchant theme or
    // another app may have wrapped getContext after us; putting the original
    // back then would silently uninstall their wrapper. Leaving ours in place is
    // the safer failure — it only tracks, it never changes behaviour.
    if (HTMLCanvasElement.prototype.getContext === installedGetContext) {
      HTMLCanvasElement.prototype.getContext = originalGetContext;
    }
    originalGetContext = null;
    installedGetContext = null;
  };

  const releaseTrackedContexts = () => {
    const contexts = trackedContexts;
    trackedContexts = [];
    for (const gl of contexts) {
      try {
        if (gl.isContextLost()) continue;
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      } catch {
        // One uncooperative context must not stop the others being released.
      }
    }
  };

  const destroyEditor = () => {
    if (!editorInstance) return;
    const instance = editorInstance;
    // Cleared first so a destroy() that re-enters via "close" can't recurse.
    editorInstance = null;
    try {
      instance.destroy();
    } catch {
      // Teardown must never break the calculator: a failed destroy costs one
      // leaked context, an exception here would cost the shopper the element.
    }
    // AFTER destroy(): let Pintura finish with the canvases first, then take the
    // contexts back. destroy() releases nothing itself, so ordering only matters
    // in that we must not pull them out from under a teardown still running.
    try {
      stopTrackingContexts();
      releaseTrackedContexts();
    } catch {
      // never break the calculator over cleanup
    }
  };

  window.openEditor = async function () {
    // Opening a second editor while one is still mounted would strand the first
    // one's context, so tear it down before replacing it.
    destroyEditor();

    // DIV-025: the editor's code arrives on first use. The loader/preview
    // toggles below used to happen synchronously after open; they now happen
    // first, so the shopper sees the spinner while the editor file downloads.
    $("#previewloader").show();
    $("#file-preview").hide();
    $(".photoEditor_Editbutton").hide();
    let pintura: NonNullable<Window["cpcPinturaEditor"]>;
    try {
      pintura = await loadPinturaEditor();
    } catch {
      // The editor could not be fetched: put the preview back exactly as it
      // was so the shopper keeps the image they uploaded and can retry.
      $("#previewloader").hide();
      $("#file-preview").show();
      $(".photoEditor_Editbutton").show();
      return;
    }

    startTrackingContexts();
    const editor = pintura.openDefaultEditor({
      src: img_data,
    });
    editorInstance = editor;

    editor.on("close", () => {
      updateAndShowPreview();
      destroyEditor();
    });

    editor.on("process", (res: any) => {
      updateAndShowPreview(res);
      // Also tear down here, not only on "close". Measured: finishing an edit
      // fires "process" but NOT "close", so a shopper who edits once and leaves
      // the page never released anything — the contexts only came back when the
      // editor was reopened. updateAndShowPreview works from res.dest (a File),
      // never from the editor, so destroying it now is safe.
      destroyEditor();
    });
  };

  const updateAndShowPreview = async (res?: any) => {
    if (!!res) {
      img_data = res.dest;
      document
        .querySelector("#file-preview")
        ?.setAttribute("src", URL.createObjectURL(res.dest));

      let fileInput = document.getElementById("photoeditor") as any;
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(res.dest);
      fileInput.files = dataTransfer.files;

      // Upload the edited image
      await window.uploadPhotoEditorImage(res.dest, element.Name);
    }

    $("#previewloader").hide();
    $("#file-preview").show();
    $(".photoEditor_Editbutton").show();
  };

  window.previewPhoto = (changeEvent: any) => {
    let input = document.getElementById("photoeditor") as any;
    const file = input.files;
    if (file) {
      const fileReader = new FileReader();
      let preview = document.getElementById("file-preview")!;
      fileReader.onload = function (event: any) {
        //Set image as base64 string
        img_data = event.target.result;
        preview.setAttribute("src", img_data);

        //open editor on file upload
        if (!!changeEvent?.type) window.openEditor();
        //show preview on receving edited file
        else {
          $("#previewloader").hide();
          $("#file-preview").show();
          $(".photoEditor_Editbutton").show();
        }
      };
      fileReader.readAsDataURL(file[0]);
    }
  };

  return `<div class='element${getElementWidthClass(element)}' data-element='${element.Identifier}'
    ${conditionalDisplay.conditionalTemplate}
    >
    <label style='color:${appState.calculatorStyling.upperTextColor}'>
    ${t(element.Name)}
    ${toolTipHtml}
    </label>
    <input
    type="file"
    id='photoeditor'
    class='calc-prop'
    data-identifier='${element.Identifier}'
    data-isrequired=${isrequired}
    name = 'cpc_upload_photoeditor_${element.Name}'
    data-property-name="${element.Name}"
    onchange='previewPhoto(event)'/>
    <input
      type='hidden'
      class='cpc-photoeditor-url-input'
      name="properties[${element.Name}]"
      data-property-name="${element.Name}"
    />
    <span class='cpc-photoeditor-upload-status' style='display:none; margin-left: 10px;'></span>
    <div class="photo-editor-container">
      <div id="previewloader" style='display:none; text-align:center; width:100%;'>
        <img src="https://app.custompricecalculator.com/loading-spinner.gif" style='width:60px; height:60px; max-width:60px; display:block; margin:0 auto;'/>
        <p>Loading Preview</p>
      </div>
      <img id="file-preview" />
      <div
      class="photoEditor_Editbutton"
      style ='display:none;background-color:${editButtonProps?.editButtonBG
    };color:${editButtonProps?.editButtonTextColor}'
      onclick="openEditor()">
      ${!!editButtonProps.editButtonText
      ? editButtonProps.editButtonText
      : "Edit"
    }
      </div>
    </div>
    </div>`;
}
